import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FullScreenSpinner } from '../../components/ui';
import apiClient from '../../services/api/apiClient';

function PaymentSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [paymentData, setPaymentData] = useState(null);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    const data = location.state;
    if (!data || !user) {
      navigate('/');
      return;
    }

    setPaymentData(data);

    // 發送確認信件
    sendConfirmationEmail(data);
  }, [location, user, navigate]);

  const sendConfirmationEmail = async (data) => {
    try {
      // 這裡會整合真實的 email 發送 API
      const emailData = {
        to: data.userEmail,
        subject: `ViralArc AI - 付款確認通知 (訂單 #${data.paymentId})`,
        template: 'payment-confirmation',
        data: {
          userName: user.name,
          planName: data.planName,
          accounts: data.accounts,
          totalPrice: data.totalPrice,
          paymentId: data.paymentId,
          paymentDate: new Date().toLocaleString('zh-TW')
        }
      };

      // 模擬發送 email
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Confirmation email sent:', emailData);
      setEmailSent(true);

      // 在實際環境中，這裡會呼叫後端 API 發送 email
      /*
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(emailData)
      });

      if (response.ok) {
        setEmailSent(true);
      }
      */
    } catch (error) {
      console.error('Failed to send confirmation email:', error);
    }
  };

  if (!paymentData) {
    return (
      <FullScreenSpinner text="載入付款資訊..." />
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: "var(--bg-primary)",
      padding: 'clamp(1rem, 4vw, 2rem)',
      color: 'var(--text-primary)'
    }}>
      <div className="responsive-container" style={{ maxWidth: '600px', textAlign: 'center' }}>
        
        {/* 成功圖示 */}
        <div style={{
          width: 'clamp(60px, 15vw, 80px)',
          height: 'clamp(60px, 15vw, 80px)',
          borderRadius: '50%',
          background: "var(--bg-primary)",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto clamp(1.5rem, 4vw, 2rem)',
          fontSize: 'var(--font-xl)'
        }}>
          ✓
        </div>

        {/* Header */}
        <div style={{ marginBottom: 'clamp(2rem, 6vw, 3rem)' }}>
          <h1 className="responsive-heading" style={{ 
            fontWeight: '700',
            color: '#3b82f6',
            marginBottom: 'clamp(0.5rem, 2vw, 1rem)'
          }}>
            付款成功！
          </h1>
          <p className="responsive-text" style={{ 
            color: 'var(--text-secondary)',
            lineHeight: '1.6'
          }}>
            感謝您選擇 ViralArc AI！<br />
            您的訂閱已成功啟用，現在可以開始使用所有功能。
          </p>
        </div>

        {/* 訂單詳情 */}
        <div style={{
          background: 'var(--bg-card)',
          borderRadius: 'clamp(16px, 3vw, 20px)',
          padding: 'clamp(1.5rem, 5vw, 2.5rem)',
          marginBottom: 'clamp(1.5rem, 4vw, 2rem)',
          border: '1px solid rgba(255,255,255,0.1)',
          textAlign: 'left'
        }}>
          <h3 className="responsive-subheading" style={{ 
            fontWeight: '600', 
            marginBottom: 'clamp(1.5rem, 4vw, 2rem)',
            color: '#3b82f6',
            textAlign: 'center'
          }}>
            訂單詳情
          </h3>
          
          <div className="responsive-grid" style={{ gap: 'clamp(0.75rem, 2vw, 1rem)' }}>
            <div className="flex-container" style={{ justifyContent: 'space-between', padding: 'clamp(0.5rem, 1.5vw, 0.75rem) 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <span className="responsive-text" style={{ color: 'var(--text-secondary)' }}>訂單編號</span>
              <span className="responsive-text" style={{ fontWeight: '600', fontFamily: 'monospace', wordBreak: 'break-all' }}>{paymentData.paymentId}</span>
            </div>
            <div className="flex-container" style={{ justifyContent: 'space-between', padding: 'clamp(0.5rem, 1.5vw, 0.75rem) 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <span className="responsive-text" style={{ color: 'var(--text-secondary)' }}>選購方案</span>
              <span className="responsive-text" style={{ fontWeight: '600' }}>{paymentData.planName}</span>
            </div>
            <div className="flex-container" style={{ justifyContent: 'space-between', padding: 'clamp(0.5rem, 1.5vw, 0.75rem) 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <span className="responsive-text" style={{ color: 'var(--text-secondary)' }}>帳號數量</span>
              <span className="responsive-text" style={{ fontWeight: '600' }}>{paymentData.accounts} 個</span>
            </div>
            <div className="flex-container" style={{ justifyContent: 'space-between', padding: 'clamp(0.5rem, 1.5vw, 0.75rem) 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <span className="responsive-text" style={{ color: 'var(--text-secondary)' }}>付款時間</span>
              <span className="responsive-text" style={{ fontWeight: '600' }}>{new Date().toLocaleString('zh-TW')}</span>
            </div>
            <div className="flex-container" style={{ justifyContent: 'space-between', padding: 'clamp(0.75rem, 2vw, 1rem) 0', fontSize: 'var(--font-xs)' }}>
              <span className="responsive-subheading" style={{ fontWeight: '600' }}>付款金額</span>
              <span style={{ 
                fontWeight: '700', 
                color: '#3b82f6',
                fontSize: 'var(--font-base)'
              }}>
                NT${paymentData.totalPrice.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Email 確認狀態 */}
        <div style={{
          background: emailSent ? 'rgba(16, 185, 129, 0.1)' : 'rgba(59, 130, 246, 0.1)',
          borderRadius: 'clamp(8px, 2vw, 12px)',
          padding: 'clamp(1rem, 4vw, 1.5rem)',
          marginBottom: 'clamp(1.5rem, 4vw, 2rem)',
          border: `1px solid ${emailSent ? 'rgba(16, 185, 129, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`
        }}>
          <div style={{ 
            fontSize: 'var(--font-base)', 
            marginBottom: '0.5rem'
          }}>
            {emailSent ? '📧' : '⏳'}
          </div>
          <div className="responsive-text" style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
            {emailSent ? '確認信件已發送' : '正在發送確認信件'}
          </div>
          <div className="responsive-text" style={{ 
            color: 'var(--text-secondary)'
          }}>
            {emailSent 
              ? `已發送到 ${paymentData.userEmail}，請查收您的信箱`
              : '請稍候，系統正在發送付款確認信件...'
            }
          </div>
        </div>

        {/* 後續步驟 */}
        <div style={{
          background: 'rgba(102, 126, 234, 0.1)',
          borderRadius: 'clamp(12px, 3vw, 16px)',
          padding: 'clamp(1.5rem, 4vw, 2rem)',
          marginBottom: 'clamp(1.5rem, 4vw, 2rem)',
          border: '1px solid rgba(102, 126, 234, 0.2)'
        }}>
          <h4 className="responsive-subheading" style={{ 
            fontWeight: '600', 
            marginBottom: 'clamp(0.75rem, 2vw, 1rem)',
            color: '#667eea'
          }}>
             接下來您可以：
          </h4>
          <ul className="responsive-text" style={{ 
            listStyle: 'none', 
            padding: 0, 
            textAlign: 'left'
          }}>
            <li className="flex-container" style={{ marginBottom: '0.5rem', alignItems: 'center', gap: 'clamp(0.5rem, 1.5vw, 0.75rem)' }}>
              <span style={{ color: '#667eea' }}>•</span>
              前往主控台設定您的社群媒體帳號
            </li>
            <li className="flex-container" style={{ marginBottom: '0.5rem', alignItems: 'center', gap: 'clamp(0.5rem, 1.5vw, 0.75rem)' }}>
              <span style={{ color: '#667eea' }}>•</span>
              上傳品牌素材和關鍵字
            </li>
            <li className="flex-container" style={{ marginBottom: '0.5rem', alignItems: 'center', gap: 'clamp(0.5rem, 1.5vw, 0.75rem)' }}>
              <span style={{ color: '#667eea' }}>•</span>
              開始自動生成和排程發文
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ color: '#667eea' }}>•</span>
              查看數據分析和成效報告
            </li>
          </ul>
        </div>

        {/* 動作按鈕 */}
        <div className="flex-container" style={{ gap: 'clamp(0.75rem, 2vw, 1rem)', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/templates')}
            style={{
              background: "white",
              border: 'none',
              color: '#3b82f6',
              padding: 'clamp(0.75rem, 2vw, 1rem) clamp(1.5rem, 4vw, 2rem)',
              borderRadius: '12px',
              fontSize: 'var(--font-xs)',
              fontWeight: '600',
              cursor: 'pointer',
              minWidth: 'clamp(160px, 35vw, 180px)',
              whiteSpace: 'nowrap',
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.4), 0 8px 20px rgba(59, 130, 246, 0.3)',
              animation: 'glow 2s ease-in-out infinite alternate'
            }}
          >
            前往主控台
          </button>
          
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'var(--text-secondary)',
              padding: 'clamp(0.75rem, 2vw, 1rem) clamp(1.5rem, 4vw, 2rem)',
              borderRadius: '12px',
              fontSize: 'var(--font-xs)',
              cursor: 'pointer',
              minWidth: 'clamp(100px, 25vw, 120px)',
              whiteSpace: 'nowrap'
            }}
          >
            返回首頁
          </button>
        </div>

        {/* 客服資訊 */}
        <div style={{ marginTop: 'clamp(2rem, 6vw, 3rem)', padding: 'clamp(1rem, 3vw, 1.5rem)', background: 'var(--bg-card)', borderRadius: 'clamp(8px, 2vw, 12px)' }}>
          <p className="responsive-text" style={{ 
            color: 'var(--text-tertiary)',
            margin: 0
          }}>
            如有任何問題或需要協助，請聯繫我們：<br />
            📧 official@viralarc-ai.com | 📞 客服專線：(02) 1234-5678
          </p>
        </div>

      </div>
    </div>
  );
}

export default PaymentSuccessPage;